import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { History, Search, ArrowUpRight, ArrowDownLeft, Send } from "lucide-react";

import { agentApi } from "@/lib/extensions-api";
import { formatNumber, formatDateTime } from "@/lib/format";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { EmptyState } from "@/components/ui/EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";
import { CopyableId } from "@/components/ui/CopyableId";
import { Badge } from "@/components/ui/Badge";

export function AgentHistoryPage() {
  const [filter, setFilter] = useState("");

  const history = useQuery({
    queryKey: ["agent-history", filter],
    queryFn: () => agentApi.history(filter || undefined),
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">Transaction History</h1>
        <p className="text-sm text-surface-500 mt-1">
          Track all points sent to customers and transfers between agents.
        </p>
      </header>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <CardTitle>All Transactions</CardTitle>
          <div className="w-full sm:w-auto">
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value.toUpperCase())}
              placeholder="Search by User ID..."
              leftIcon={<Search className="h-4 w-4" />}
            />
          </div>
        </CardHeader>
        
        <CardBody className="p-0 border-t border-surface-200">
          {history.isLoading ? (
            <div className="p-5 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : (history.data ?? []).length === 0 ? (
            <EmptyState 
              icon={History} 
              title="No transactions found"
              description={filter ? "Try searching a different User ID." : "Start distributing or transferring points to see them here."} 
            />
          ) : (
            <div className="w-full overflow-x-auto">
              <Table className="min-w-[600px]">
                <THead>
                  <TR>
                    <TH>Type</TH>
                    <TH>User / Agent ID</TH>
                    <TH className="text-right">Points</TH>
                    <TH>Note</TH>
                    <TH>Date & Time</TH>
                  </TR>
                </THead>
                <tbody>
                  {history.data!.map((tx) => {
                    
                    // 🛠️ Determine Transaction Type formatting
                    const transactionType = String(tx.transaction_type);
                    const isTransferIn = transactionType === "agent_to_agent_transfer_in";
                    const isTransferOut = transactionType === "agent_to_agent_transfer_out";

                    return (
                      <TR key={tx.id} className="hover:bg-surface-50 transition-colors">
                        <TD>
                          {isTransferIn ? (
                            <Badge tone="success" className="flex w-max items-center gap-1">
                              <ArrowDownLeft className="h-3 w-3" /> Received
                            </Badge>
                          ) : isTransferOut ? (
                            <Badge tone="warning" className="flex w-max items-center gap-1">
                              <ArrowUpRight className="h-3 w-3" /> Transferred Out
                            </Badge>
                          ) : (
                            <Badge tone="info" className="flex w-max items-center gap-1">
                              <Send className="h-3 w-3" /> Sent to Customer
                            </Badge>
                          )}
                        </TD>
                        
                        <TD>
                          <CopyableId value={tx.recipient_user_id ?? "Admin"} />
                        </TD>
                        
                        <TD className={`text-right font-bold font-mono ${isTransferIn ? "text-success-700" : "text-surface-900"}`}>
                          {isTransferIn ? "+" : "-"}{formatNumber(tx.points)}
                        </TD>
                        
                        <TD className="text-xs text-surface-600 max-w-[200px] truncate">
                          {tx.reference || "—"}
                        </TD>
                        
                        <TD className="text-[11px] font-medium text-surface-500 whitespace-nowrap">
                          {formatDateTime(tx.created_at)}
                        </TD>
                      </TR>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}